import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import Avatar from '../common/Avatar';
import { useLanguage } from '../../context/LanguageContext';
import { LangGraphMessage } from '../../types';
import Button from '../common/Button';

// Format date utility function
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

interface ChatMessageProps {
  message: LangGraphMessage;
  onSendMessage?: (content: string) => void;
  isLastMessage?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onSendMessage, isLastMessage = false }) => {
  const { labels } = useLanguage();

  // Extract content from LangGraph message (handle both string and array formats)
  const getContent = () => {
    console.log("message.content: ", message.content);
    console.log("message: ", JSON.stringify(message, null, 2));

    let content = '';
    if (message.tool_calls?.length > 0) {
      content = message.tool_calls.map((toolCall: any) => toolCall.name).join('\n');
    } else if (Array.isArray(message.content)) {
      content = message.content
        .filter((chunk: any) => chunk.type === 'text')
        .map((chunk: any) => chunk.text)
        .join('');
    } else {
      content = message.content;
    }

    // Always remove [YES/NO] from the displayed content
    return content.replace(/\[YES\/NO\]/gi, '').trim();
  };

  // Check if message contains [YES/NO] pattern
  const hasYesNoPrompt = () => {
    const content = typeof message.content === 'string' ? message.content : 
                   Array.isArray(message.content) ? 
                   message.content.filter((chunk: any) => chunk.type === 'text').map((chunk: any) => chunk.text).join('') : '';
    return /\[YES\/NO\]/gi.test(content);
  };

  // Choose appropriate name based on message type
  const getName = () => {
    switch (message.type) {
      case 'human':
        return labels.userRole;
      case 'ai':
        // For AI messages, check the agent name to determine the specific role
        const agentName = message.name;
        if (agentName === 'planning_agent') return labels.planningAgentRole;
        if (agentName === 'research_agent') return labels.researchAgentRole;
        if (agentName === 'query_storage_agent') return labels.storageAgentRole;
        if (agentName === 'agent' || agentName === 'supervisor') return labels.mainAgentRole;
        // For unknown agents, show original agent name if available, otherwise show "Agent"
        return agentName || labels.agentRole;
      default:
        // For unknown types, show original agent name if available, otherwise show "Unknown"
        return message.name || labels.unknownAgent;
    }
  };

  // Format message container based on type
  const getContainerClasses = () => {
    const baseClasses = "rounded-lg p-4 max-w-4xl shadow-sm";

    if (message.type === 'human') {
      return `${baseClasses} bg-primary-900 text-white border-2 border-primary-900 ml-auto`;
    }

    // All AI agents now use the same styling as the main agent
    return `${baseClasses} bg-white border-2 border-primary-200 text-primary-900`;
  };
  
  // Get timestamp
  const getTimestamp = () => {
    if (message.additional_kwargs?.timestamp) {
      return new Date(message.additional_kwargs.timestamp);
    }
    return new Date();
  };
  
  return (
    <motion.div
      className={`flex mb-6 ${message.type === 'human' ? 'justify-end' : 'justify-start'}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={`flex flex-col ${message.type === 'human' ? 'items-end' : 'items-start'} max-w-3xl`}>
        <div className={`flex items-center mb-2 ${message.type === 'human' ? 'flex-row-reverse' : 'flex-row'}`}>
          <Avatar
            name={getName()}
            size="sm"
            className={`${message.type === 'human' ? 'ml-2' : 'mr-2'}`}
          />
          <span className="text-sm font-semibold text-primary-700">{getName()}</span>
          <span className="mx-2 text-primary-400">•</span>
          <span className="text-xs text-primary-500">{formatDate(getTimestamp())}</span>
        </div>

        <div className={getContainerClasses()}>
          {message.type === 'human' ? (
            // User messages: render as plain text
            <p className="whitespace-pre-wrap">{getContent()}</p>
          ) : (
            // AI messages: render as markdown
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  // Custom styling for different markdown elements
                  h1: ({ children }) => <h1 className="text-xl font-bold text-primary-900 mb-3">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-lg font-bold text-primary-900 mb-2 mt-4">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-base font-semibold text-primary-800 mb-2 mt-3">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-sm font-semibold text-primary-800 mb-1 mt-2">{children}</h4>,
                  p: ({ children }) => <p className="mb-2 text-primary-900">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-primary-900">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-primary-900">{children}</strong>,
                  em: ({ children }) => <em className="italic text-primary-900">{children}</em>,
                  code: ({ children }) => <code className="bg-primary-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                  pre: ({ children }) => <pre className="bg-primary-100 p-3 rounded-lg overflow-x-auto mb-2">{children}</pre>,
                  blockquote: ({ children }) => <blockquote className="border-l-4 border-primary-300 pl-4 italic text-primary-700 mb-2">{children}</blockquote>,
                  hr: () => <hr className="border-primary-200 my-4" />,
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline font-medium hover:no-underline transition-colors"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {getContent()}
              </ReactMarkdown>
            </div>
          )}
          
          {/* Show Yes/No buttons only for the last message that contains [YES/NO] */}
          {isLastMessage && hasYesNoPrompt() && onSendMessage && (
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendMessage(labels.yes)}
                className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
              >
                {labels.yes}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendMessage(labels.no)}
                className="border-primary-200 text-primary-700 hover:bg-primary-50 hover:border-primary-300"
              >
                {labels.no}
              </Button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage;